import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { debouncedSearch } from '@vsolv/utils/rxjs-operators';
import { ToastService } from '@vsolv/vectors-ui/alert';
import {
  BadgeColumn,
  PaginationConfig,
  SortableTableComponent,
  SortParams,
  TableColumn,
  TextColumn,
  UserInfoColumn,
} from '@vsolv/vectors/table';
import { ServiceProvider } from '@wsphere/service-providers/domain';
import { SecurityService } from '@wsphere/staff/web';
import { parsePhoneNumber } from 'libphonenumber-js';
import moment from 'moment';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  from,
  map,
  of,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { ServiceProviderService } from '../../services';

type ServiceProviderTableFilters = Pick<ServiceProvider.ListServiceProvidersQueryRequest, 'search' | 'status'>;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-service-providers-page',
  templateUrl: './service-providers.page.html',
  standalone: false,
})
export class ServiceProvidersPage extends SortableTableComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private securitySvc: SecurityService,
    private serviceProviderSvc: ServiceProviderService
  ) {
    super();
  }

  private destroy$ = new Subject<void>();

  hasLoadError = false;

  // Global check: providers are tenant-wide, so this must not follow the distributor picker.
  readonly canCreate$ = from(this.securitySvc.hasAccess('svp_CreateServiceProvider', null)).pipe(shareReplay(1));

  readonly statuses = Object.values(ServiceProvider.Status);
  readonly searchInput$ = new Subject<string>();

  filters: ServiceProviderTableFilters = this.getFiltersFromUrl();
  private filters$ = new BehaviorSubject<ServiceProviderTableFilters>(this.filters);

  readonly pagination$ = new BehaviorSubject({ currentPage: 1, itemsPerPage: 10 });

  readonly serviceProviders$ = combineLatest([this.pagination$, this.filters$, this.sort$]).pipe(
    switchMap(([pagination, filters, sort]) =>
      this.serviceProviderSvc
        .list$({
          page: pagination.currentPage,
          limit: pagination.itemsPerPage,
          search: filters.search,
          status: filters.status,
          ...(sort && { sortBy: sort.sortBy, sortOrder: sort.sortOrder }),
        })
        .pipe(
          tap(() => (this.hasLoadError = false)),
          // Emit an empty page so the skeleton is replaced and the stream stays alive for the next filter change.
          catchError(() => {
            this.hasLoadError = true;
            this.toastSvc.show({
              type: 'error',
              title: 'Something went wrong',
              text: 'We were unable to load service providers. Please try again.',
            });
            return of(this.emptyResult(pagination.itemsPerPage));
          })
        )
    ),
    shareReplay(1)
  );

  readonly paginationConfig$ = this.serviceProviders$.pipe(
    map(
      (result): PaginationConfig => ({
        totalItems: result.meta.totalItems ?? 0,
        itemsPerPage: result.meta.itemsPerPage,
        currentPage: result.meta.currentPage,
        pageSizes: [10, 20, 50],
      })
    )
  );

  // Headers double as the backend sort keys, so they must match its column map.
  columns: TableColumn<unknown>[] = [
    new UserInfoColumn<ServiceProvider.Model>(
      { header: 'Service Provider', fitContent: true, align: 'left', sortable: true },
      serviceProvider => ({
        name: serviceProvider.name,
        email: serviceProvider.email ?? '',
        copyText: serviceProvider.name,
      })
    ),

    new TextColumn<ServiceProvider.Model>({ header: 'Phone Number' }, serviceProvider => ({
      text: this.formatPhone(serviceProvider.phone),
      classes: 'truncate max-w-[15vw]',
    })),

    new TextColumn<ServiceProvider.Model>({ header: 'Main Contact' }, serviceProvider => ({
      text: serviceProvider.mainContact || '-',
      classes: 'truncate max-w-[15vw]',
    })),

    new BadgeColumn<ServiceProvider.Model>({ header: 'Status', sortable: true }, serviceProvider => ({
      text: serviceProvider.status.charAt(0) + serviceProvider.status.slice(1).toLowerCase(),
      displayStatusIcon: true,
      theme: this.getTheme(serviceProvider.status),
    })),

    new TextColumn<ServiceProvider.Model>({ header: 'Last activity', sortable: true }, serviceProvider => ({
      text: moment(serviceProvider.modified).format('MMM DD, YYYY'),
    })),

    new TextColumn<ServiceProvider.Model>({ header: 'Date created', sortable: true }, serviceProvider => ({
      text: moment(serviceProvider.created).format('MMM DD, YYYY'),
    })),
  ];

  get hasActiveFilters() {
    return !!this.filters.search || !!this.filters.status;
  }

  ngOnInit() {
    this.searchInput$
      .pipe(debouncedSearch(), takeUntil(this.destroy$))
      .subscribe(search => this.refreshTable({ search }));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  override onSortChanged(sort: SortParams) {
    super.onSortChanged(sort);
    this.pagination$.next({ ...this.pagination$.value, currentPage: 1 });
  }

  onStatusChange(status: ServiceProvider.Status | readonly ServiceProvider.Status[] | null) {
    const resolved = Array.isArray(status) ? (status[0] ?? null) : status;
    this.refreshTable({ status: resolved ?? undefined });
  }

  getTheme(status: ServiceProvider.Status) {
    return status === ServiceProvider.Status.ACTIVE ? 'success' : 'info';
  }

  navigateTo(serviceProviderId: string | null) {
    if (serviceProviderId) void this.router.navigate([serviceProviderId], { relativeTo: this.route });
  }

  private refreshTable(changes: ServiceProviderTableFilters) {
    this.filters = { ...this.filters, ...changes };
    // New filters shrink the results — go back to page 1.
    if (this.pagination$.value.currentPage !== 1) this.pagination$.next({ ...this.pagination$.value, currentPage: 1 });

    this.filters$.next(this.filters);
    this.syncFiltersToUrl();
  }

  private emptyResult(itemsPerPage: number): ServiceProvider.ListServiceProvidersQueryResponse {
    return {
      items: [],
      meta: { itemCount: 0, totalItems: 0, itemsPerPage, totalPages: 0, currentPage: 1 },
    };
  }

  // Phone is free text from the API, so never let a bad value break the table.
  private formatPhone(phone: string | null) {
    if (!phone) return '-';
    try {
      return parsePhoneNumber(phone, 'US')?.formatNational() ?? phone;
    } catch {
      return phone;
    }
  }

  private syncFiltersToUrl() {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { search: this.filters.search || null, status: this.filters.status || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private getFiltersFromUrl(): ServiceProviderTableFilters {
    const params = this.route.snapshot.queryParamMap;
    const search = params.get('search')?.trim() || undefined;
    const statusParam = params.get('status');
    const status = (Object.values(ServiceProvider.Status) as string[]).includes(statusParam ?? '')
      ? (statusParam as ServiceProvider.Status)
      : undefined;

    return { ...(search && { search }), ...(status && { status }) };
  }
}
